import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";


export default function SignInModal({ open, onOpenChange, onSwitchToRegister }) {
  const [view, setView] = useState("signin"); // signin | forgot-email | forgot-otp | forgot-newpw
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resetAll = () => {
    setView("signin"); setResetEmail(""); setCooldown(0); setOtp(""); setResetToken(""); setNewPw(""); setConfirmPw("");
  };
  const close = (v) => { if (!v) resetAll(); onOpenChange(v); };

  const submit = async () => {
    if (!email || !password) return toast.error("Enter email and password");
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      login(r.data.token, r.data.owner);
      toast.success(`Welcome back, ${r.data.owner.property_name}`);
      close(false);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign in failed");
    } finally { setBusy(false); }
  };

  const sendOtp = async () => {
    if (!resetEmail.trim()) return toast.error("Enter your registered email");
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: resetEmail.trim() });
      setView("forgot-otp"); setOtp("");
      setCooldown(r.data.resend_after_seconds || 60);
      toast.success(`OTP sent to ${resetEmail.trim()} (valid 5 minutes)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) return toast.error("Enter the 6-digit OTP from your email");
    setBusy(true);
    try {
      const r = await api.post("/auth/verify-otp", { email: resetEmail.trim(), otp });
      setResetToken(r.data.reset_token);
      setView("forgot-newpw");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP verification failed");
    } finally { setBusy(false); }
  };

  const resetPw = async () => {
    if (!/^(?=.*[A-Za-z])(?=.*\d)\S{8,}$/.test(newPw))
      return toast.error("Password must be at least 8 characters with letters and numbers");
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { reset_token: resetToken, new_password: newPw });
      toast.success("Password updated — sign in with your new password");
      setEmail(resetEmail.trim()); setPassword("");
      resetAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const titles = {
    signin: ["Sign in", "Access your owner dashboard."],
    "forgot-email": ["Forgot password", "Enter your registered email to receive an OTP."],
    "forgot-otp": ["Enter OTP", `We sent a 6-digit OTP to ${resetEmail}. It expires in 5 minutes.`],
    "forgot-newpw": ["Set new password", "Choose a new password for your account."],
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="signin-modal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{titles[view][0]}</DialogTitle>
          <DialogDescription>{titles[view][1]}</DialogDescription>
        </DialogHeader>

        {view === "signin" && (
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@example.com" data-testid="signin-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="signin-password-input" />
              <button className="text-xs text-indigo-600 hover:underline" onClick={() => setView("forgot-email")} data-testid="signin-forgot-password-link">
                Forgot password?
              </button>
            </div>
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={submit} disabled={busy} data-testid="signin-submit-button">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <p className="text-xs text-center text-slate-500">
              New here?{" "}
              <button className="text-indigo-600 hover:underline" onClick={onSwitchToRegister} data-testid="signin-switch-register">
                Register your property
              </button>
            </p>
          </div>
        )}

        {view === "forgot-email" && (
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Registered email</Label>
              <Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendOtp()} placeholder="owner@example.com" data-testid="forgot-email-input" />
            </div>
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={sendOtp} disabled={busy} data-testid="forgot-send-otp-button">
              {busy ? "Sending…" : "Send OTP"}
            </Button>
            <BackLink onClick={resetAll} />
          </div>
        )}

        {view === "forgot-otp" && (
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">6-digit OTP</Label>
              <Input value={otp} inputMode="numeric" onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && verifyOtp()} maxLength={6} placeholder="••••••" className="font-mono tracking-[0.4em] text-center text-lg" data-testid="forgot-otp-input" />
            </div>
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={verifyOtp} disabled={busy} data-testid="forgot-verify-otp-button">
              {busy ? "Verifying…" : "Verify OTP"}
            </Button>
            <button className="text-xs text-indigo-600 hover:underline disabled:text-slate-400 disabled:no-underline w-full text-center" onClick={sendOtp} disabled={busy || cooldown > 0} data-testid="forgot-resend-otp-button">
              {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
            </button>
            <BackLink onClick={resetAll} />
          </div>
        )}

        {view === "forgot-newpw" && (
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">New password</Label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min 8 characters, letters + numbers" data-testid="forgot-new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Confirm new password</Label>
              <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Re-enter new password" data-testid="forgot-confirm-password-input" />
            </div>
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={resetPw} disabled={busy} data-testid="forgot-reset-button">
              <KeyRound className="h-4 w-4 mr-1.5" /> {busy ? "Updating…" : "Update password"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BackLink({ onClick }) {
  return (
    <button className="text-xs text-slate-500 hover:text-slate-700 w-full text-center" onClick={onClick} data-testid="forgot-back-link">
      ← Back to sign in
    </button>
  );
}

